import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Lock, ArrowRight } from "lucide-react";
import EB1AssessmentResults from "./results/EB1AssessmentResults";

interface AssessmentPreviewProps {
  data: any;
}

const AssessmentPreview = ({ data }: AssessmentPreviewProps) => {
  const navigate = useNavigate();

  // Sample EB-1 scores for demonstration
  const eb1Scores = {
    extraordinaryAbility: 85,
    nationalRecognition: 75,
    publications: 90,
    leadership: 80,
    commercialSuccess: 70
  };

  if (!data?.results) {
    return (
      <Card className="w-full max-w-4xl mx-auto bg-card/50 backdrop-blur-sm">
        <CardContent className="p-8 text-center">
          <p className="text-lg text-muted-foreground">
            No assessment results available. Please complete the assessment form.
          </p>
          <Button 
            onClick={() => navigate("/eligibility")} 
            className="mt-4"
          >
            Start Assessment
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      {/* Display EB-1 Assessment Results if applicable */}
      {data.visaPathway?.id === "eb1" && (
        <EB1AssessmentResults scores={eb1Scores} />
      )}

      {/* Locked Features Section */}
      <div className="bg-primary/5 p-6 rounded-lg space-y-6 border border-primary/10">
        <div className="flex items-start gap-4">
          <Lock className="w-5 h-5 text-primary mt-1" />
          <div>
            <h3 className="text-lg font-semibold mb-2">
              Create an Account to Access Your Full Report
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  <span>Detailed eligibility analysis</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  <span>Program-specific recommendations</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  <span>Documentation checklist</span>
                </li>
              </ul>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  <span>Timeline and next steps</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  <span>Expert guidance and support</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  <span>Priority processing options</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <Button 
          size="lg"
          className="w-full"
          onClick={() => navigate("/signin", { 
            state: { 
              returnUrl: "/eligibility",
              preserveFormData: true 
            } 
          })}
        >
          Create Account to Access Full Report
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

export default AssessmentPreview;